import {useState} from 'react'
import {Users, Plus, Search} from 'lucide-react'
import {useApp} from '../../context/AppContext'
import type {Team} from '../../types'
import TeamCard from './TeamCard'
import TeamForm from './TeamForm'
import ConfirmDialog from '../common/ConfirmDialog'
import PageHeader from '../common/PageHeader'

export default function TeamList() {
  const {state, apiDeleteTeam} = useApp()
  const [showForm, setShowForm] = useState(false)
  const [editTeam, setEditTeam] = useState<Team | undefined>()
  const [deleteTeamId, setDeleteTeamId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [membership, setMembership] = useState('all')
  const query = search.trim().toLocaleLowerCase('pt-BR')
  const teams = state.teams
    .filter((team) => {
      const people = state.users.filter(
        (user) => team.memberIds.includes(user.id) || team.leadId === user.id
      )
      const matchesSearch = [team.name, team.description, ...people.map((person) => person.name)]
        .join(' ')
        .toLocaleLowerCase('pt-BR')
        .includes(query)
      return (
        matchesSearch &&
        (membership === 'all' ||
          (membership === 'empty' ? team.memberIds.length === 0 : !team.leadId))
      )
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  const members = new Set(state.teams.flatMap((team) => team.memberIds))
  const deletingTeam = state.teams.find((team) => team.id === deleteTeamId)
  const clearFilters = () => {
    setSearch('')
    setMembership('all')
  }
  const handleCloseForm = () => {
    setShowForm(false)
    setEditTeam(undefined)
  }

  return (
    <div className='devops-page'>
      <PageHeader
        eyebrow='Organização'
        icon={Users}
        title='Equipes'
        description='Organize as pessoas, defina lideranças e acompanhe as entregas de todos os quadros de cada equipe.'
        meta={
          <>
            <span>{state.teams.length} equipes</span>
            <span>{members.size} pessoas vinculadas</span>
            <span>{state.teams.filter((team) => !team.leadId).length} sem liderança</span>
          </>
        }
        actions={
          <button onClick={() => setShowForm(true)} className='devops-primary-button'>
            <Plus size={16} />
            Nova equipe
          </button>
        }
      />

      <div className='devops-surface p-4 flex flex-wrap items-center gap-3'>
        <div className='flex items-center gap-2 flex-1 min-w-[200px]'>
          <Search size={17} className='text-slate-400 shrink-0' />
          <input
            aria-label='Buscar equipes por nome, descrição ou pessoa'
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder='Buscar equipe, descrição ou pessoa...'
            className='w-full px-3 py-2 rounded-xl border border-slate-200 text-sm'
          />
        </div>
        <select
          aria-label='Filtrar composição das equipes'
          value={membership}
          onChange={(event) => setMembership(event.target.value)}
          className='px-3 py-2 rounded-xl border border-slate-200 text-sm'
        >
          <option value='all'>Todas as equipes</option>
          <option value='empty'>Sem membros</option>
          <option value='no-lead'>Sem liderança</option>
        </select>
        {(search || membership !== 'all') && (
          <button className='devops-secondary-button' onClick={clearFilters}>
            Limpar filtros
          </button>
        )}
        <span className='text-xs text-slate-500' role='status'>
          {teams.length} de {state.teams.length} equipes
        </span>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
        {teams.length === 0 && (
          <div className='devops-empty-state col-span-full'>
            <Users size={28} />
            <div>
              <h3>
                {state.teams.length
                  ? 'Nenhuma equipe corresponde à busca'
                  : 'Organize sua primeira equipe'}
              </h3>
              <p>
                {state.teams.length
                  ? 'Tente outro nome ou remova os filtros de composição.'
                  : 'Reúna os membros e defina quem coordena as próximas entregas.'}
              </p>
            </div>
            <button
              className='devops-primary-button'
              onClick={state.teams.length ? clearFilters : () => setShowForm(true)}
            >
              {state.teams.length ? 'Limpar filtros' : 'Criar equipe'}
            </button>
          </div>
        )}
        {teams.map((team) => (
          <TeamCard
            key={team.id}
            team={team}
            onEdit={() => {
              setEditTeam(team)
              setShowForm(true)
            }}
            onDelete={() => setDeleteTeamId(team.id)}
          />
        ))}
      </div>
      {showForm && <TeamForm team={editTeam} onClose={handleCloseForm} />}
      <ConfirmDialog
        isOpen={deleteTeamId !== null}
        title='Excluir equipe'
        message={
          'Excluir a equipe “' +
          (deletingTeam?.name || '') +
          '”? Revise os ' +
          state.boards.filter((board) => board.teamId === deleteTeamId).length +
          ' quadros vinculados antes de continuar. Esta ação não pode ser desfeita.'
        }
        confirmLabel='Excluir equipe'
        onConfirm={async () => {
          if (deleteTeamId) await apiDeleteTeam(deleteTeamId)
          setDeleteTeamId(null)
        }}
        onCancel={() => setDeleteTeamId(null)}
      />
    </div>
  )
}
